import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { exportToCsv } from "../../lib/exportCsv";
import { BarChart3, Users, Mountain, MessageCircle, Store } from "lucide-react";

const METRICS = [
    { id: "users", label: "New users", icon: Users },
    { id: "adventures", label: "Adventures", icon: Mountain },
    { id: "comments", label: "Comments", icon: MessageCircle },
    { id: "listings", label: "Listings", icon: Store },
];
const WINDOWS = [
    { id: 7, label: "7d" },
    { id: 30, label: "30d" },
    { id: 90, label: "90d" },
];

export default function AnalyticsTab() {
    const [kpis, setKpis] = useState(null);
    const [metric, setMetric] = useState("users");
    const [window_, setWindow_] = useState(30);
    const [series, setSeries] = useState([]);
    const [loadingKpis, setLoadingKpis] = useState(true);
    const [loadingSeries, setLoadingSeries] = useState(true);

    const loadKpis = useCallback(async () => {
        setLoadingKpis(true);
        try {
            const { data } = await api.get("/admin/analytics/kpis", {
                params: { window_days: window_ },
            });
            setKpis(data);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoadingKpis(false);
        }
    }, [window_]);

    const loadSeries = useCallback(async () => {
        setLoadingSeries(true);
        try {
            const { data } = await api.get("/admin/analytics/timeseries", {
                params: { metric, window_days: window_ },
            });
            setSeries(data.series || []);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoadingSeries(false);
        }
    }, [metric, window_]);

    useEffect(() => { loadKpis(); }, [loadKpis]);
    useEffect(() => { loadSeries(); }, [loadSeries]);

    const onExport = () =>
        exportToCsv(
            series,
            [{ key: "day", label: "Day" }, { key: "count", label: "Count" }],
            `analytics-${metric}-${window_}d`
        );

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                    <BarChart3 className="w-5 h-5 text-ember-orange" />
                    <h3 className="font-display font-bold text-lg text-midnight-pine">Platform KPIs</h3>
                </div>
                <div className="flex gap-1 bg-snowcap border border-ridge-line rounded-lg p-1">
                    {WINDOWS.map((w) => (
                        <button
                            key={w.id}
                            data-testid={`analytics-window-${w.id}`}
                            onClick={() => setWindow_(w.id)}
                            className={`px-3 py-1.5 rounded-md text-xs font-bold uppercase tracking-widest ${
                                window_ === w.id ? "bg-midnight-pine text-white" : "text-slate-gray hover:text-granite"
                            }`}
                        >
                            Last {w.label}
                        </button>
                    ))}
                </div>
            </div>

            {loadingKpis || !kpis ? (
                <div className="text-slate-gray text-sm">Loading KPIs…</div>
            ) : (
                <div className="grid md:grid-cols-3 gap-4" data-testid="analytics-kpi-grid">
                    <KpiCard label="New users" main={kpis.users.new} sub={`of ${kpis.users.total} total`} />
                    <KpiCard label="New adventures" main={kpis.adventures.new} sub={`${kpis.adventures.hidden} hidden`} />
                    <KpiCard label="Engagement" main={kpis.engagement.likes + kpis.engagement.saves + kpis.engagement.comments} sub={`${kpis.engagement.likes} likes · ${kpis.engagement.saves} saves · ${kpis.engagement.comments} comments`} />
                    <KpiCard label="Marketplace" main={kpis.marketplace.new} sub={`${kpis.marketplace.total} listings`} />
                    <KpiCard label="Reports open" main={kpis.reports.open} sub={`${kpis.reports.total} lifetime`} highlight={kpis.reports.open > 0} />
                    <KpiCard label="Active ads" main={kpis.ads.active} sub={`${kpis.ads.pending_review} pending review`} />
                </div>
            )}

            <div className="bg-snowcap border border-ridge-line rounded-lg p-5">
                <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                    <h3 className="font-display font-bold text-lg text-midnight-pine">Daily trend</h3>
                    <div className="flex items-center gap-2">
                        <div className="flex gap-1 bg-alpine-mist border border-ridge-line rounded-md p-1">
                            {METRICS.map((m) => (
                                <button
                                    key={m.id}
                                    data-testid={`analytics-metric-${m.id}`}
                                    onClick={() => setMetric(m.id)}
                                    className={`inline-flex items-center gap-1.5 px-3 py-1 rounded text-[11px] font-bold uppercase tracking-widest ${
                                        metric === m.id ? "bg-midnight-pine text-white" : "text-slate-gray hover:text-granite"
                                    }`}
                                >
                                    <m.icon className="w-3 h-3" /> {m.label}
                                </button>
                            ))}
                        </div>
                        <button
                            data-testid="analytics-export"
                            onClick={onExport}
                            disabled={series.length === 0}
                            className="px-3 py-1.5 rounded-md bg-midnight-pine text-white text-xs font-bold hover:bg-deep-evergreen disabled:opacity-40"
                        >
                            Export CSV
                        </button>
                    </div>
                </div>
                {loadingSeries ? (
                    <div className="text-slate-gray text-sm">Loading series…</div>
                ) : (
                    <Sparkline data={series} />
                )}
            </div>
        </div>
    );
}

function KpiCard({ label, main, sub, highlight }) {
    return (
        <div
            data-testid={`analytics-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}
            className={`bg-snowcap border rounded-lg p-5 ${highlight ? "border-ember-orange/60 ring-2 ring-ember-orange/20" : "border-ridge-line"}`}
        >
            <div className="text-xs font-bold uppercase tracking-widest text-slate-gray mb-1">{label}</div>
            <div className="font-display font-black text-3xl text-midnight-pine tabular-nums">{main}</div>
            <div className="text-xs text-slate-gray mt-1">{sub}</div>
        </div>
    );
}

/**
 * Minimal inline SVG sparkline — avoids pulling a chart library for one view.
 */
function Sparkline({ data }) {
    if (data.length === 0) {
        return <div className="text-slate-gray text-sm text-center py-10">No activity in this window.</div>;
    }
    const max = Math.max(1, ...data.map((d) => d.count));
    const w = 800;
    const h = 180;
    const pad = 24;
    const step = (w - pad * 2) / Math.max(1, data.length - 1);
    const points = data.map((d, i) => {
        const x = pad + i * step;
        const y = h - pad - (d.count / max) * (h - pad * 2);
        return `${x},${y}`;
    }).join(" ");
    const total = data.reduce((a, b) => a + b.count, 0);
    return (
        <div>
            <div className="flex items-baseline justify-between mb-2">
                <div className="text-xs text-slate-gray">
                    Total in window: <span className="font-bold text-granite">{total}</span>
                </div>
                <div className="text-xs text-slate-gray">Peak: <span className="font-bold text-granite">{max}</span></div>
            </div>
            <svg
                data-testid="analytics-sparkline"
                viewBox={`0 0 ${w} ${h}`}
                className="w-full h-48"
                preserveAspectRatio="none"
            >
                <polyline
                    fill="none"
                    stroke="#e3793b"
                    strokeWidth="2"
                    points={points}
                />
                {data.map((d, i) => {
                    const x = pad + i * step;
                    const y = h - pad - (d.count / max) * (h - pad * 2);
                    return <circle key={i} cx={x} cy={y} r="2.5" fill="#e3793b" />;
                })}
            </svg>
        </div>
    );
}
